"use client"

import { useEffect, useState } from "react"
import { useAuth } from "@/contexts/auth-context"
import { apiRequest, apiRequestWithAuth, getStoredToken, isTokenValid } from "@/lib/api-config"
import { useToast } from "@/hooks/use-toast"
import { DashboardLayout } from "@/components/dashboard-layout"
import { UserHomeView } from "@/components/user-home-view"
import NotLoggedIn from "@/components/not-logged-in"


export default function UserDashboard() {
  const { token: contextToken, logout } = useAuth()
  const { toast } = useToast()
  const [userStatus, setUserStatus] = useState<any>(null)
  const [userAccount, setUserAccount] = useState<any>(null)
  const [sanity, setSanity] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [announcement, setAnnouncement] = useState<any>(null)

  const getToken = () => {
    return contextToken || getStoredToken()
  }

  useEffect(() => {
    const token = getToken()
    if (token && isTokenValid(token)) {
      fetchUserData()
    } else {
      toast({
        variant: "destructive",
        title: "认证失败",
        description: "请重新登录",
      })
    }
    fetchAnnouncement()
  }, [contextToken])

  const fetchAnnouncement = async () => {
    try {
      const result = await apiRequest("/getAnnouncement", { method: "GET" })
      if (result.code === 200) {
        setAnnouncement(result.data)
      }
    } catch (error) {
    }
  }

  const fetchUserData = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({
        variant: "destructive",
        title: "认证失败",
        description: "请重新登录",
      })
      return
    }

    try {
      // 并行请求三个独立接口：总耗时取最慢者，而非串行累加（原实现约 3 倍耗时）
      const [statusResult, accountResult, sanityResult] = await Promise.all([
        apiRequestWithAuth("/showMyStatus", token, { method: "GET" }),
        apiRequestWithAuth("/showMyAccount", token, { method: "GET" }),
        apiRequestWithAuth("/showMySan", token, { method: "GET" }),
      ])

      if (statusResult.code === 200) {
        setUserStatus(statusResult.data)
      } else {
        toast({
          variant: "destructive",
          title: "获取状态失败",
          description: statusResult.msg || "无法获取用户状态",
        })
      }

      if (accountResult.code === 200) {
        setUserAccount(accountResult.data)
      } else {
        toast({
          variant: "destructive",
          title: "获取账号信息失败",
          description: accountResult.msg || "无法获取账号信息",
        })
      }

      if (sanityResult.code === 200) {
        setSanity(String(sanityResult.data))
      } else {
        toast({
          variant: "destructive",
          title: "获取理智状态失败",
          description: sanityResult.msg || "无法获取理智状态",
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "无法连接到服务器",
      })
    }
  }

  const handleStartNow = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/startNow", token, {
        method: "POST",
      })
      if (result.code === 200) {
        toast({
          variant: "success",
          title: "操作成功",
          description: "已成功开始作战",
        })
        await fetchUserData()
      } else {
        toast({
          variant: "destructive",
          title: "操作失败",
          description: result.msg || "无法开始作战",
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "操作失败",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleForceHalt = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/forceHalt", token, {
        method: "POST",
      })
      if (result.code === 200) {
        toast({
          variant: "success",
          title: "操作成功",
          description: "已成功停止作战",
        })
        await fetchUserData()
      } else {
        toast({
          variant: "destructive",
          title: "操作失败",
          description: result.msg || "无法停止作战",
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "操作失败",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleFreezeToggle = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token) || !userAccount) return
    setLoading(true)
    try {
      let api = userAccount.freeze ? "/unfreezeMyAccount" : "/freezeMyAccount"
      const result = await apiRequestWithAuth(api, token, { method: "POST" })
      if (result.code === 200) {
        toast({
          variant: "success",
          title: userAccount.freeze ? "解冻成功" : "冻结成功",
          description: userAccount.freeze ? "账户已解冻" : "账户已冻结",
        })
        await fetchUserData()
      } else {
        toast({
          variant: "destructive",
          title: "操作失败",
          description: result.msg || (userAccount.freeze ? "解冻失败" : "冻结失败"),
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "操作失败",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    window.location.href = "/"
  }

  const token = getToken()
  if (!token || !isTokenValid(token)) {
    return (
      <NotLoggedIn />
    )
  }

  return (
    <DashboardLayout>
      <UserHomeView token={token} account={userAccount} status={userStatus}
        sanity={sanity} announcement={announcement} loading={loading}
        onStart={handleStartNow} onStop={handleForceHalt}
        onFreeze={handleFreezeToggle} onLogout={handleLogout} />
    </DashboardLayout>
  )
}
